import { debounce } from 'radash';
import { useRef, useEffect, useState } from 'react';
import { FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { IoCloseCircle } from 'react-icons/io5';
import { MdAccessTime, MdError } from 'react-icons/md';
import { Loading, Button } from '../../components';
import { Modal } from '../../components/modal';
import { Error } from '../../components/error';
import { useJobData } from '../../state';
import { colors, styles } from '../../constants';
import { Cost } from './Cost';

const border = '1px solid #ccc';

export const Percent = ({ percent }) => {
  if (percent === undefined) {
    return null;
  }

  const p = percent * 100;

  const height = 24;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          marginTop: 4,
          width: '100%',
          background: '#ccc',
          borderRadius: 4,
          height,
        }}
      >
        <div
          style={{
            color: 'white',
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            fontSize: 12,
            width: '100%',
            lineHeight: height + 'px',
          }}
        >
          {!!p && <span>{Math.floor(p)}%</span>}
          {!p && <Loading size="xs" color="gray" />}
        </div>
        <div
          style={{
            width: `${p}%`,
            background: colors.green,
            height: '100%',
            borderRadius: 4,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

const Canvas = ({ job, jobs }) => {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const wrapperRef = useRef(null);

  const [tick, setTick] = useState(0);
  const [times, setTimes] = useState([]);
  const [layout, setLayout] = useState([[]]);
  const [assigned, setAssigned] = useState({});
  const [height, setHeight] = useState(100);
  const [width, setWidth] = useState(window.innerWidth);

  const topGap = 12;
  const barHeight = 2;
  const gap = 0.5;
  const rowHeight = barHeight + gap;

  useEffect(() => {
    const handle = debounce({ delay: 200 }, () => {
      setWidth(wrapperRef?.current?.offsetWidth);
    });
    window.addEventListener('resize', handle);
    handle();
    return () => window.removeEventListener('resize', handle);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 20);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const times_ = [];
    const layout_ = [[]];
    const assigned_ = { ...assigned };

    const now = new Date().getTime();
    const overlaps = (a, b) => a.started < b.finished && a.finished > b.started;

    // console.log('ggh Draw based on jobs:', jobs);

    for (const job of jobs) {
      const state = job.state;
      const started = job.started;
      const finished = job.finished || now;

      times_.push(started);
      times_.push(finished);

      let rowNum;

      if (assigned_[job.jobId]) {
        rowNum = assigned_[job.jobId];
      } else {
        // console.log('no row assigned:', job.jobId, layout_);

        for (const [idx, row] of layout_.entries()) {
          let ok = true;
          for (const item of row) {
            if (overlaps(item, { started, finished, state })) {
              // console.log('overlap:', item, job);
              ok = false;
            }
          }
          if (ok) {
            rowNum = idx;
            break;
          }
        }

        if (rowNum === undefined) {
          rowNum = layout_.length;
        }
        // layout_[rowNum].push({ started, finished });
        // assigned_[job.jobId] = rowNum;
      }

      while (rowNum >= layout_.length) {
        layout_.push([]);
      }

      layout_[rowNum].push({ started, finished, state });
    }

    setAssigned(assigned_);
    setLayout(layout_);
    setTimes(times_);
    setHeight(topGap + layout_.length * rowHeight);
  }, [tick, jobs, rowHeight]);

  useEffect(() => {
    const start = Math.min(...times);
    const end = Math.max(...times);

    const xInterval = 10_000;

    const totalDuration = Math.ceil((end - start) / xInterval) * xInterval;

    const dpr = window.devicePixelRatio || 1;
    const getCtx = (c) => {
      c.style.width = width + 'px';
      c.style.height = height + 'px';
      c.width = width * dpr;
      c.height = height * dpr;
      const x = c.getContext('2d');
      x.scale(dpr, dpr);
      return x;
    };

    const offscreen = offscreenRef.current;
    const offCtx = getCtx(offscreen);

    if (!offscreen.width || !offscreen.height) {
      return;
    }

    offCtx.clearRect(0, 0, width, height);

    const drawRect = (x, y, w, h, color) => {
      offCtx.fillStyle = color;
      offCtx.fillRect(x, y, w, h);
    };

    for (let i = 0; i < layout.length; i++) {
      const row = layout[i];
      for (const item of row) {
        const offset = item.started - start;
        const duration = item.finished - item.started;

        const x = Math.round(width * (offset / totalDuration));
        const y = (rowHeight + gap) * i;
        const w = Math.round(width * (duration / totalDuration));
        const h = barHeight;

        const color =
          {
            completed: colors.green,
            failed: colors.lightError,
          }[item.state] || '#aaa';

        drawRect(x, topGap + y, w, h, color);
      }
    }

    offCtx.font = '10px Arial';
    offCtx.fillStyle = '#888';

    for (let i = 0; i < totalDuration; i += xInterval) {
      const x = width * (i / totalDuration);

      const pretty = (seconds) => {
        // return `${seconds}s`;
        return `${Math.floor(seconds / 60)}m${seconds % 60}s`;
      };

      offCtx.fillText(`${pretty(i / 1000)}`, x, 8);
    }

    const canvas = canvasRef.current;
    const ctx = getCtx(canvas);
    ctx.clearRect(0, 0, width, height);
    ctx.scale(1 / dpr, 1 / dpr);
    ctx.drawImage(offscreen, 0, 0);
  }, [tick]);

  const hasChildren = jobs?.length > 0;

  // let startingNode;
  // if (noChildren) {
  //   startingNode = (
  //     <div>
  //       <Loading>Starting...</Loading>
  //     </div>
  //   );
  // }

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      {/*startingNode*/}

      <canvas
        style={{
          display: hasChildren ? null : 'none',
          border: '1px solid #ccc',
          padding: '2px 2px 0 2px',
          borderRadius: 2,
        }}
        ref={canvasRef}
        width={width}
        height={height}
      />

      <canvas
        style={{ display: 'none', border: '1px solid #ccc', padding: '2px 2px 0 2px', borderRadius: 2 }}
        ref={offscreenRef}
        width={width}
        height={height}
      />

      {/*
          <pre>{JSON.stringify(assigned, null, 2)}</pre>
          <pre>{JSON.stringify(jobs, null, 2)}</pre>
          <pre>{jobs.length}</pre>
          <pre>{jobs.filter(it => !!it.finished).length}</pre>
          <pre>{JSON.stringify(layout, null, 2)}</pre>
        */}
    </div>
  );
};

const JobsList = ({ jobs }) => {
  const [expanded, setExpanded] = useState();
  const [visible, setVisible] = useState([]);
  const [tick, setTick] = useState(0);
  const [opened, setOpened] = useState();

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const cutoff = 100;

  useEffect(() => {
    setVisible([...(jobs || [])].sort((a, b) => b.started - a.started).slice(0, expanded ? jobs.length : cutoff));
  }, [jobs, tick, expanded]);

  const open = (jobId) => {
    setOpened(opened == jobId ? null : jobId);
  };

  const fontSize = 10;
  const now = new Date().getTime();

  const nodes = visible.map((job, idx) => {
    const finished = !!job.finished;
    const msec = Math.max(job.started, job.finished || now) - job.started;
    const sharedStyle = { marginRight: 4 };

    const iconSize = 12;

    const icon = {
      waiting: <MdAccessTime size={iconSize} />,
      delayed: <MdAccessTime size={iconSize} />,
      paused: <MdAccessTime size={iconSize} />,
      'waiting-children': <MdAccessTime />,
      active: <Loading size="xs" />,
      completed: <FaCheckCircle size={iconSize} color={colors.green} />,
      failed: <MdError size={iconSize + 2} color={colors.error} />,
    }[job?.state];

    const activeStyle = {
      background: colors.lightBlue,
    };

    return (
      <div
        key={idx}
        style={{
          display: 'flex',
          alignItems: 'center',
          borderTop: idx ? '1px solid #eee' : '',
          padding: 0,
          gap: 2,
          cursor: 'pointer',
          ...(opened == job.jobId ? activeStyle : {}),
        }}
        onClick={() => open(job.jobId)}
      >
        <div style={{ ...sharedStyle, paddingLeft: 2, width: 18, textAlign: 'center' }}>{icon}</div>
        <div style={{ ...sharedStyle, width: 40, fontSize, fontFamily: 'monospace', textAlign: 'right' }}>
          {(msec / 1e3).toFixed(1)}s
        </div>
        <div style={{ ...sharedStyle, width: 100, fontSize, textAlign: 'left' }}>{job.name}</div>
        <div style={{ ...sharedStyle, width: 80, fontSize, fontFamily: 'monospace', textAlign: 'left' }}>
          {job.jobId}
        </div>
        <div style={{ ...sharedStyle, ...styles.textOverflowStyle, fontSize, width: '100%' }}>{job.url}</div>
      </div>
    );
  });

  const borderRadius = '4px';

  const showMoreNode = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        background: 'white',
        borderTop: border,
        position: 'sticky',
        padding: 5,
        bottom: 0,
        zIndex: 5,
        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
      }}
    >
      <Button trans small gray onClick={() => setExpanded(true)}>
        Show All {jobs.length} Jobs
      </Button>
    </div>
  );

  const showLessNode = (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        borderTop: border,
        background: 'white',
        padding: 5,
        display: 'flex',
        justifyContent: 'center',
        borderRadius: `0 0 ${borderRadius} ${borderRadius}`,
      }}
    >
      <Button trans small gray onClick={() => setExpanded(false)}>
        Collapse
      </Button>
    </div>
  );

  const openedNode = opened && <JobDetails jobId={opened} onClose={() => setOpened(null)} />;

  const maxHeight = expanded ? 600 : 400;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, overflow: 'hidden' }}>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius,
            maxHeight,
            overflowY: expanded ? 'scroll' : 'hidden',
            width: opened ? '50%' : '100%',
          }}
        >
          {nodes}
          {!expanded && jobs.length > cutoff && showMoreNode}
          {expanded && showLessNode}
        </div>

        {openedNode && (
          <div style={{ width: '50%', border: '1px solid #ccc', borderRadius, maxHeight, overflowY: 'scroll' }}>
            {openedNode}
          </div>
        )}
      </div>
    </div>
  );
};

const JobDetails = ({ jobId, onClose }) => {
  const { job, loading } = useJobData(jobId);

  if (!job?.name) {
    return (
      <div style={{ padding: 10 }}>
        <Loading>Loading {job?.id}...</Loading>
      </div>
    );
  }

  const cls =
    {
      visit: JobDetailsVisit,
      runCompletion: JobDetailsRunCompletion,
    }[job.name] || JobDetailsJson;

  return (
    <div style={{}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: 'white',
          borderRadius: '4px 4px 0 0',
          borderBottom: border,
          padding: '5px 10px',
          zIndex: 2,
        }}
      >
        <div>
          <b>
            {job.name} {job.id}
            {job.state && `: ${job.state}`}
          </b>
        </div>
        <IoCloseCircle style={{ cursor: 'pointer' }} size={24} onClick={onClose} />
      </div>

      <div style={{ padding: 10 }}>{cls({ job })}</div>
    </div>
  );
};

const JobDetailsJson = ({ job }) => {
  return (
    <div style={{ overflow: 'hidden' }}>
      <pre>{JSON.stringify(job, null, 2)}</pre>
    </div>
  );
};

const JobDetailsRunCompletion = ({ job }) => {
  const promptStyle = {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: 12,
    overflow: 'hidden',
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: 10,
  };

  return (
    <div>
      <h3>Prompt</h3>
      <Modal>
        <div style={{ ...promptStyle, maxHeight: 200 }}>{job.args?.prompt}</div>
        <div style={{ ...promptStyle, width: '100%', maxWidth: 800 }}>{job.args?.prompt}</div>
      </Modal>

      <h3>Answer</h3>
      <div style={{ ...promptStyle }}>{JSON.stringify(job?.answer?.partial, null, 2)}</div>

      <h3>Cost</h3>
      <Cost job={job} expanded />

      {/*
      <pre>{JSON.stringify(job.answer, null, 2,)}</pre>
      */}
    </div>
  );
};

const JobDetailsVisit = ({ job }) => {
  const fontSize = 12;

  const urlNodes = (job?.results?.urls || []).map((it) => (
    <li>
      <a href={it} target="_blank">
        {it}
      </a>
    </li>
  ));

  return (
    <div>
      <div>
        <a href={job?.args?.url} target="_blank">
          {job?.args?.url}
        </a>
      </div>

      <Error message={job?.error} style={{ marginTop: 10 }} />

      <h3>Content</h3>
      <ul style={{ fontSize }}>
        <li>
          <a href={job?.results?.htmlUrl} target="_blank">
            Full HTML
          </a>
        </li>
        <li>
          <a href={job?.results?.slimUrl} target="_blank">
            Slim HTML
          </a>
        </li>
        <li>
          <a href={job?.results?.textUrl} target="_blank">
            Text only
          </a>
        </li>
        <li>
          <a href={job?.results?.jsonUrl} target="_blank">
            JSON only
          </a>
        </li>
      </ul>

      <h3>URLs Found</h3>
      <ul style={{ fontSize }}>
        {urlNodes.slice(0, 20)}
        {urlNodes.length > 20 && <li>{urlNodes.length - 20} more not shown</li>}
      </ul>

      <h3>Cost</h3>
      <Cost job={job} expanded />

      {/*
      <pre>{JSON.stringify(job, null, 2)}</pre>
      */}
    </div>
  );
};

export const Progress = ({ job }) => {
  const progress = job.progress;

  if (!job) {
    return null;
  }

  const children = (progress?.children?.jobs || []).sort((a, b) => a.started - b.started);
  const percent = (progress?.progress || 0) / (progress?.total || 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Percent state={job.state} percent={percent} />
      <Canvas job={job} jobs={children} />
      <JobsList jobs={children} />
    </div>
  );
};
